import * as React from 'react';

import {useEffect, useState} from 'react';
import {Reservation, Warehouse} from '../../../shared/CommonInterfaces';
import WarehouseService from '../../../shared/services/WarehouseService';
import SelectorWithCheckbox from '../../../shared/SelectorWithCheckbox';
import WarehouseSelector from '../../../shared/WarehouseSelector';
import {InboundFilterKeys, InboundState} from '../InboundInterfaces';
import GenerateLPNs from '../../../dropoffs/lpns/GenerateLPNs';
import InboundsTable from './data-grid/InboundsTable';
import InboundStatusTabs from './InboundStatusTab';
import InboundFilter from './InboundFilter';

interface InboundsIndexProps {
  selectedWarehouse: Warehouse;
  authenticityToken: string;
  isShipper: boolean;
  availableWarehouses?: Warehouse[];
  onWarehouseChange?: (warehouse: Warehouse) => void;
}

const ALL_RESERVATIONS_ID_VALUE = '-1';

const InboundsIndex: React.FC<InboundsIndexProps> = (props) => {
  // State to hold the active filters, managed by this parent component.
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // Loading state is lifted up to be shared between the table and the filter.
  const [isLoading, setIsLoading] = useState(true);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedReservationId, setSelectedReservationId] = useState<string>(ALL_RESERVATIONS_ID_VALUE);
  // State to manage the selected status tab. Default to `null` for "All".
  const [selectedStatus, setSelectedStatus] = useState<InboundState | null>(null);

  const warehouseService = new WarehouseService(props.authenticityToken);

  // Derive behavior from isShipper
  const showGenerateLpns = !props.isShipper;

  useEffect(() => {
    getReservations();
    setSelectedReservationId(ALL_RESERVATIONS_ID_VALUE);
    handleFilterChange(InboundFilterKeys.ReservationId, '');
  }, [props.selectedWarehouse]);

  const getReservations = async () => {
    const response = await warehouseService.getReservationsForWarehouse(props.selectedWarehouse.id);
    setReservations(response.data.reservations);
  };

  const reservationDropdown = () => {
    const options = reservations.map((reservation) => {
      return {
        name: reservation.name,
        value: `${reservation.id}`
      };
    });

    options.unshift({name: 'All', value: `${ALL_RESERVATIONS_ID_VALUE}`});

    const onSelect = (value: string) => {
      setSelectedReservationId(value);
      if (value === ALL_RESERVATIONS_ID_VALUE) {
        handleFilterChange(InboundFilterKeys.ReservationId, '');
      } else {
        handleFilterChange(InboundFilterKeys.ReservationId, value);
      }
    };

    return (
      <SelectorWithCheckbox
        options={options}
        selected={[selectedReservationId]}
        onSelect={onSelect}
        prefixText="Reservation: "
      />
    );
  };

  // Handler for when a status tab is clicked.
  const handleStatusSelect = (status: InboundState | null) => {
    setSelectedStatus(status);
    handleFilterChange(InboundFilterKeys.Status, status);
  };

  // Handler to update filters. This is passed as a prop to InboundFilter.
  const handleFilterChange = (key: string, value: string | null) => {
    setActiveFilters((currentFilters) => {
      const newValue = value || '';

      // Check if the filter state would actually change
      const currentValue = currentFilters[key] || '';
      const hasChange = currentValue !== newValue;

      if (!hasChange) {
        return currentFilters;
      }

      const newFilters = {...currentFilters};
      if (newValue) {
        newFilters[key] = newValue;
      } else {
        delete newFilters[key];
      }
      return newFilters;
    });
  };

  return (
    <div className="inbounds-index-page container-fluid">
      <div id="inbound-list">
        <div className="title-header">
          <div className="control-buttons-row">
            <h1>Inbound Deliveries</h1>
            <div className="row-group text-right">
              {props.isShipper && (
                <a href="/s/dropoffs/new" className="btn cta">
                  <i className="fa fa-plus"></i> Schedule a Delivery
                </a>
              )}
              {showGenerateLpns && <GenerateLPNs />}
            </div>
          </div>

          <div className="header-filter-top-row">
            <ul className="list-inline">
              {props.isShipper && props.availableWarehouses && props.availableWarehouses.length > 0 && (
                <li>
                  <WarehouseSelector
                    selectedWarehouse={props.selectedWarehouse}
                    activeWarehouses={props.availableWarehouses}
                    onSelect={props.onWarehouseChange}
                  />
                </li>
              )}
              <li>{reservationDropdown()}</li>
            </ul>
          </div>

          <hr />

          <InboundFilter activeFilters={activeFilters} onFilterChange={handleFilterChange} loading={isLoading} />
        </div>

        <div className="content-container">
          <div className="control-buttons-row">
            <div className="compact-status-tabs">
              <InboundStatusTabs selectedStatus={selectedStatus} onStatusSelect={handleStatusSelect} />
            </div>
          </div>

          <div className="inbounds-table-container">
            <InboundsTable
              warehouse={props.selectedWarehouse}
              activeFilters={activeFilters}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isShipper={props.isShipper}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InboundsIndex;
